import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import WorkItemCopyLinkButton from "./WorkItemCopyLinkButton";
import {
  buildWorkItemSubmitBody,
  buildBugDetailsFromIssueState,
} from "../../utils/buildWorkItemSubmitBody";
import { isWorkItemVersionConflictError } from "../../utils/workItemVersionConflict";
import { useDispatch, useSelector } from "react-redux";
import { selectIssuesData } from "../../Redux/Slices/issuesSlice";
import { selectUsersData } from "../../Redux/Slices/usersSlice";
import {
  fetchWorkItemById,
  fetchProjectsData,
  fetchWorkItemTimeLogs,
  updateWorkItem,
  listWorkItemAttachments,
  getWorkItemAttachmentDownloadUrl,
  deleteWorkItemAttachment,
} from "../../Redux/Thunks/projectManagementThunk";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import { fetchUsersData } from "../../Redux/Thunks/usersThunk";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import { isClientOnHold } from "../../constants/clientOnHoldRules";
import {
  mapSystemListItemToOption,
  getListFromApiResponse,
  preferListFromPropOrStore,
  getAllowedProjects,
  mapProjectToCreateTaskOption,
  mapUserToAssigneeOptionForDropdown,
  labelsToSimpleOptions,
  PRIORITY_LABEL_FALLBACKS,
  SEVERITY_LABEL_FALLBACKS,
  ENVIRONMENT_LABEL_FALLBACKS,
  TYPE_ICON_MAP,
  resolveWorkItemTypeDisplayName,
  buildProjectStatusOptionsByCategory,
  resolveProjectStatusOptionValue,
  getWorkItemClientProjectParts,
} from "./issueMappers";
import {
  DEFAULT_WORK_ITEM_STATUS_CATEGORY_FILTER_OPTIONS,
  remapWorkItemStatusForTargetProject,
  getProjectWorkflowStatusesList,
} from "@/constants/workItemStatusCategoryTheme";
import WorkItemTypeIcon from "./WorkItemTypeIcon";
import EpicChildIssuesPanel from "./EpicChildIssuesPanel";
import ActivityTabs from "./ActivityTabs";
import { mapOptionsToMentionUsers } from "./mentionUtils";
import MiniEditor from "./MiniEditor";
import FileUploadPanel from "./FileUploadPanel";
import LogTimeModal from "./LogTimeModal/LogTimeModal";
import "./issueDetail.css";
import Button from "../../Utility/Button/Button";
import Input from "../../Utility/Input/Input";
import Typography from "../../Utility/Typography";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import { showSuccess, showError } from "../../utils/toast";
import { getApiErrorMessage, isSilentRequestAbortError } from "../../utils/apiErrorMessage";
import { fetchProjectStatuses } from "../../Redux/Thunks/projectStatusesThunk";
import { selectProjectStatusesResponse } from "../../Redux/Slices/projectStatusesSlice";
import {
  selectProjectsAllListData,
  selectProjectsData,
  selectProjectsListForDropdown,
  selectProjectsLoading,
} from "../../Redux/Slices/projectsManagementSlice";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import {
  selectWorkspaceProjectId,
  selectWorkspaceProjectName,
} from "../../Redux/Slices/projectDashboardSlice";
import {
  mapWorkItemFromApiPayload,
  mapWorkItemAttachmentsListResponse,
  mapWorkItemAttachmentUrlResponse,
  parseWorkItemTimeLogsPayload,
} from "../Projects/projectMappers";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import {
  formatDateForInput,
  toDateOnlyApiParam,
  DISPLAY_DATE_FORMAT_PICKER_MOMENT,
} from "../../utils/dateUtils";
import SingleDatePicker from "../../Utility/DatePickerRange/SingleDatePicker";
import {
  aggregateWorkItemTimeLogsTodayAndWeek,
  formatCompactLoggedDuration,
} from "../../utils/workItemLoggedTimeDisplay";
import {
  parseWorkItemEstimateInputToMinutes,
  sanitizeWorkItemEstimateInput,
} from "../../utils/workItemEstimateInput";
import Loader from "../../Utility/Loader/Loader";
import ErrorMsg from "../../Utility/ErrorMsg";
import { WorkItemPlanningDatesSchema } from "../../FormsValidations/Validations";
import { get } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { normalizeWorkItemFromApi } from "../Projects/projectManagementNormalizers";
import { copyWorkItemDetailLink } from "../../utils/workItemDetailUrl";
import {
  findProjectDisplayNameInList,
  resolveProjectDisplayName,
} from "../../utils/resolveProjectDisplayName";

function parseNonNegativeInt(value, fallback = 0) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.round(n);
}

/** Avoid GET /projects/:id when work item or Redux already has the display name. */
function canResolveProjectNameWithoutFetch(projectSlug, sources) {
  const { workItem, workspaceProjectId, workspaceProjectName, projectLists } = sources;
  const fromWorkItem = String(
    workItem?.projectName ?? workItem?.project?.name ?? workItem?.project?.projectName ?? ""
  ).trim();
  if (fromWorkItem) return true;

  const slug = String(projectSlug ?? "").trim();
  if (slug && workspaceProjectId != null && String(workspaceProjectId).trim() === slug) {
    if (String(workspaceProjectName ?? "").trim()) return true;
  }

  for (const list of projectLists) {
    if (findProjectDisplayNameInList(slug, list)) return true;
  }
  return false;
}

export default function IssueDetail({
  projectSlug,
  issueId: propIssueId,
  /**
   * `modal` — inside {@link WorkItemDetailModal};
   * `standalone` — shareable full-page route (no app shell);
   * `page` — default in-app page layout.
   */
  presentation = "page",
  /** Modal host: show overlay until bootstrap completes (`pageLoading` false). */
  onBootstrapLoadingChange,
  /** After successful Save — e.g. refetch project list and close modal (`Promise` ok). */
  onWorkItemSaved,
  /** Modal / embedded: open another work item (e.g. parent epic) in the same shell. */
  onOpenRelatedWorkItem,
  /** After save when the work item's project changed — host updates modal URL / query. */
  onProjectIdChange,
  /** Host page header (e.g. shared work item route) — receives resolved project title. */
  onProjectDisplayNameChange,
  /** Project workspace tab encoded in copied links (`issues`, `boards`, …). */
  _linkReturnTab = "issues",
}) {
  const dispatch = useDispatch();
  const { can } = usePermissions();
  const canEditWorkItem = can(PERMISSION_KEYS.WORK_ITEMS_EDIT);
  const canCreateWorkItem = can(PERMISSION_KEYS.WORK_ITEMS_CREATE);
  const readOnlyWorkItem = !canEditWorkItem;
  const canLogTimeEntry = can(PERMISSION_KEYS.TIME_ENTRY_CREATE);
  const location = useLocation();
  const issuesData = useSelector(selectIssuesData);
  const usersData = useSelector(selectUsersData);
  const projectsDropdown = useSelector(selectProjectsListForDropdown);
  const projectsData = useSelector(selectProjectsData);
  const allProjectsData = useSelector(selectProjectsAllListData);
  const projectsLoading = useSelector(selectProjectsLoading);
  const clientsData = useSelector(selectClientsData);
  const workspaceProjectName = useSelector(selectWorkspaceProjectName);
  const workspaceProjectId = useSelector(selectWorkspaceProjectId);
  const [resolvedProjectNameFromList, setResolvedProjectNameFromList] = useState("");
  const prevProjectIdForParentResetRef = useRef(null);
  const statusRemapPendingRef = useRef(false);

  const projectsFromStore = useMemo(() => {
    const allList = getListFromApiResponse(allProjectsData);
    const pagedList = getListFromApiResponse(projectsData);
    if (allList.length > 0 && pagedList.length > 0) {
      const byId = new Map();
      for (const p of allList) {
        const id = String(p?.id ?? p?.projectId ?? "");
        if (id) byId.set(id, p);
      }
      for (const p of pagedList) {
        const id = String(p?.id ?? p?.projectId ?? "");
        if (id && !byId.has(id)) byId.set(id, p);
      }
      return { list: Array.from(byId.values()) };
    }
    return allProjectsData ?? projectsData ?? projectsDropdown;
  }, [allProjectsData, projectsData, projectsDropdown]);

  const projectsList = preferListFromPropOrStore(null, projectsFromStore);
  const clientsList = preferListFromPropOrStore(null, clientsData);

  useEffect(() => {
    if (!projectsList?.length) dispatch(fetchProjectsData({ getAll: true }));
    if (!clientsList?.length) dispatch(fetchClientsData({ getAll: true }));
  }, [dispatch, projectsList?.length, clientsList?.length]);

  const urlParams = new URLSearchParams(location.search);
  const issueIdFromUrl = urlParams.get("id");
  const issueId = propIssueId || issueIdFromUrl || "";

  const issueFromStore = issuesData?.list?.find(
    (issue) =>
      issue.id?.toString() === issueId ||
      issue.issueKey === issueId ||
      (issueId && issue.id === issueId)
  );

  // Prefer work item loaded by GET /projects/:projectId/work-items/:workItemId
  const [workItemById, setWorkItemById] = useState(null);
  const [workItemByIdError, setWorkItemByIdError] = useState(null);
  const [pageLoading, setPageLoading] = useState(() => Boolean(projectSlug && issueId));
  const [bootstrapError, setBootstrapError] = useState(null);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [loggedTimeSummary, setLoggedTimeSummary] = useState({ todayMinutes: 0, weekMinutes: 0 });

  // Load work item, dropdown reference data, statuses, users, then parent epics + attachments
  useEffect(() => {
    if (!projectSlug || !issueId) {
      setPageLoading(false);
      return;
    }
    let cancelled = false;
    setPageLoading(true);
    setBootstrapError(null);
    setWorkItemByIdError(null);
    setResolvedProjectNameFromList("");

    (async () => {
      try {
        // Statuses load in a separate effect (`selectedProjectIdForUi`) — do not duplicate here
        // or ApiBase may cancel one request and fail the bootstrap.
        const workItemRaw = await dispatch(
          fetchWorkItemById({ projectId: projectSlug, workItemId: issueId })
        ).unwrap();

        if (cancelled) return;

        const workItem = mapWorkItemFromApiPayload(workItemRaw);
        setWorkItemById(workItem);

        const settled = await Promise.allSettled([
          dispatch(fetchSystemList({ listType: "PRIORITY", context: "issue-detail" })).unwrap(),
          dispatch(fetchSystemList({ listType: "SEVERITY", context: "issue-detail" })).unwrap(),
          dispatch(fetchSystemList({ listType: "ENVIRONMENT", context: "issue-detail" })).unwrap(),
          dispatch(
            fetchSystemList({ listType: "WORK_ITEM_TYPE", context: "issue-detail" })
          ).unwrap(),
          dispatch(fetchUsersData({ getAll: true })).unwrap(),
        ]);

        if (cancelled) return;

        const pickList = (result) =>
          result.status === "fulfilled" && Array.isArray(result.value) ? result.value : [];
        const [priorityListRaw, severityListRaw, environmentListRaw, workItemTypesRaw] =
          settled.map(pickList);

        const projectLists = [projectsDropdown?.list, allProjectsData?.list, projectsData?.list];
        if (
          !canResolveProjectNameWithoutFetch(projectSlug, {
            workItem,
            workspaceProjectId,
            workspaceProjectName,
            projectLists,
          })
        ) {
          let nameFromLists = null;
          for (const list of projectLists) {
            nameFromLists = findProjectDisplayNameInList(projectSlug, list);
            if (nameFromLists) break;
          }
          if (nameFromLists) {
            setResolvedProjectNameFromList(nameFromLists);
          } else {
            const projectsRaw = await dispatch(fetchProjectsData({ getAll: true }))
              .unwrap()
              .catch(() => null);
            if (!cancelled && projectsRaw) {
              const list = getListFromApiResponse(projectsRaw);
              const name = findProjectDisplayNameInList(projectSlug, list);
              if (name) setResolvedProjectNameFromList(name);
            }
          }
        }
        setPriorityList(priorityListRaw);
        setSeverityList(severityListRaw);
        setEnvironmentList(environmentListRaw);
        setWorkItemTypes(workItemTypesRaw);

        const wid = workItem?.id ?? issueId;
        const attachListRaw = await dispatch(
          listWorkItemAttachments({
            projectId: String(projectSlug),
            workItemId: String(wid),
          })
        ).unwrap();
        if (cancelled) return;
        const attachList = mapWorkItemAttachmentsListResponse(attachListRaw);
        setExistingAttachments(Array.isArray(attachList) ? attachList : []);

        setPageLoading(false);
      } catch (err) {
        if (cancelled || isSilentRequestAbortError(err)) return;
        const msg = getApiErrorMessage(err, "Failed to load issue");
        setWorkItemByIdError(msg);
        setBootstrapError(msg);
        setPageLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Bootstrap once per project/work item; project name uses Redux lists at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [dispatch, projectSlug, issueId]);

  useEffect(() => {
    onBootstrapLoadingChange?.(pageLoading);
  }, [pageLoading, onBootstrapLoadingChange]);

  const detailSource = workItemById ?? issueFromStore;
  const resolvedWorkItemIdForLogs = detailSource?.id ?? issueId;

  const breadcrumbProjectDisplayName = useMemo(
    () =>
      resolveProjectDisplayName(projectSlug, {
        workItemProjectName: detailSource?.projectName,
        workItemProject: detailSource?.project,
        workspaceProjectId,
        workspaceProjectName,
        fetchedProjectName: resolvedProjectNameFromList,
        projectLists: [projectsDropdown?.list, allProjectsData?.list, projectsData?.list],
      }),
    [
      projectSlug,
      detailSource?.projectName,
      detailSource?.project,
      workspaceProjectId,
      workspaceProjectName,
      resolvedProjectNameFromList,
      projectsDropdown?.list,
      allProjectsData?.list,
      projectsData?.list,
    ]
  );

  useEffect(() => {
    onProjectDisplayNameChange?.(breadcrumbProjectDisplayName);
  }, [breadcrumbProjectDisplayName, onProjectDisplayNameChange]);

  const [issue, setIssue] = useState({
    title: "",
    description: "",
    type: "Task",
    typeId: null,
    status: "Open",
    priority: "Medium",
    assignee: "",
    reporter: "",
    estimate: "",
    storyPoints: "",
    severity: "",
    environment: "",
    reproduction: "",
    startDate: "",
    dueDate: "",
    progressPercent: "",
    projectId: "",
    statusCategory: "",
  });

  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  /** Remounts {@link FileDropzone} after upload so its internal list clears. */
  const [attachmentDropzoneKey, setAttachmentDropzoneKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dateFieldErrors, setDateFieldErrors] = useState({});
  const [isLogTimeModalOpen, setIsLogTimeModalOpen] = useState(false);
  const [timeEntryDeniedKey, setTimeEntryDeniedKey] = useState(null);
  const [priorityList, setPriorityList] = useState([]);
  const [severityList, setSeverityList] = useState([]);
  const [environmentList, setEnvironmentList] = useState([]);
  const [workItemTypes, setWorkItemTypes] = useState([]);
  const [parentEpicOptions, setParentEpicOptions] = useState([]);
  const [parentFieldError, setParentFieldError] = useState("");

  const selectedProjectIdForUi =
    (issue.projectId && String(issue.projectId).trim()) ||
    (projectSlug && String(projectSlug).trim()) ||
    "";

  /** Persisted project for activity APIs — not `issue.projectId` (dropdown changes before save). */
  const activityProjectId =
    String(
      detailSource?.projectId ?? detailSource?.project?.id ?? detailSource?.project_id ?? ""
    ).trim() ||
    (projectSlug && String(projectSlug).trim()) ||
    "";

  const statusesResponse = useSelector((state) =>
    selectedProjectIdForUi ? selectProjectStatusesResponse(state, selectedProjectIdForUi) : null
  );

  const allowedProjects = useMemo(
    () => getAllowedProjects(projectsList, clientsList, isClientOnHold),
    [projectsList, clientsList]
  );

  const projectOptions = useMemo(() => {
    const base = allowedProjects
      .map((p) => mapProjectToCreateTaskOption(p))
      .filter((o) => o.value !== "" && o.value != null);
    const currentId = selectedProjectIdForUi;
    if (!currentId) return base;
    const currentOption = projectsList
      .map((p) => mapProjectToCreateTaskOption(p))
      .find((o) => String(o.value) === String(currentId));
    if (
      currentOption?.value &&
      !base.some((o) => String(o.value) === String(currentOption.value))
    ) {
      return [currentOption, ...base];
    }
    return base;
  }, [allowedProjects, projectsList, selectedProjectIdForUi]);

  const projectsLoadingOrEmpty = projectsLoading || !projectsList?.length;

  useEffect(() => {
    if (!activityProjectId || !resolvedWorkItemIdForLogs) {
      setLoggedTimeSummary({ todayMinutes: 0, weekMinutes: 0 });
      return undefined;
    }
    let cancelled = false;
    dispatch(
      fetchWorkItemTimeLogs({
        projectId: activityProjectId,
        workItemId: resolvedWorkItemIdForLogs,
        requestScope: "sidebar-summary",
      })
    )
      .unwrap()
      .then((raw) => {
        if (cancelled) return;
        const { list } = parseWorkItemTimeLogsPayload(raw);
        const { todayMinutes, weekMinutes } = aggregateWorkItemTimeLogsTodayAndWeek(list ?? []);
        setLoggedTimeSummary({ todayMinutes, weekMinutes });
      })
      .catch((err) => {
        if (cancelled || isSilentRequestAbortError(err)) return;
        setLoggedTimeSummary({ todayMinutes: 0, weekMinutes: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, activityProjectId, resolvedWorkItemIdForLogs, activityRefreshKey]);

  useEffect(() => {
    if (!selectedProjectIdForUi) return;
    dispatch(fetchProjectStatuses({ projectId: selectedProjectIdForUi, context: "issue-detail" }));
  }, [dispatch, selectedProjectIdForUi]);

  useEffect(() => {
    if (!selectedProjectIdForUi) {
      setParentEpicOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const parentEpicRes = await get(
          API_ENDPOINTS.PROJECTS.GET_WORK_ITEM_EPICS(selectedProjectIdForUi)
        );
        if (cancelled) return;
        const raw = parentEpicRes?.data ?? parentEpicRes;
        const dataWrapper = raw?.data ?? raw;
        const rawList = Array.isArray(dataWrapper)
          ? dataWrapper
          : Array.isArray(dataWrapper?.data)
            ? dataWrapper.data
            : [];
        const epicList = rawList.map(normalizeWorkItemFromApi);
        const options = epicList
          .map((wi) => ({
            label: `${wi.issueKey || wi.itemKey || wi.id} - ${wi.title ?? wi.summary ?? "Untitled"}`,
            value: wi?.id != null ? String(wi.id) : "",
          }))
          .filter((o) => o.value);
        setParentEpicOptions(options);
      } catch {
        if (!cancelled) setParentEpicOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectIdForUi]);

  // Update issue when detail source (GET-by-id or store) is available
  useEffect(() => {
    const source = detailSource;
    if (source) {
      const environmentOptionsFromList =
        environmentList && environmentList.length
          ? environmentList.map((item) => mapSystemListItemToOption(item))
          : [];
      const toEnvironmentDisplay = (raw) => {
        if (raw == null) return "";
        const tokens = String(raw)
          .split(",")
          .map((s) => String(s).trim())
          .filter(Boolean);
        if (tokens.length === 0) return "";
        return tokens
          .map((token) => {
            const found = environmentOptionsFromList.find(
              (opt) =>
                String(opt.value).toLowerCase() === token.toLowerCase() ||
                String(opt.id ?? "").toLowerCase() === token.toLowerCase() ||
                String(opt.label).toLowerCase() === token.toLowerCase()
            );
            return found?.label ?? token;
          })
          .join(", ");
      };

      const reporterName =
        typeof source.reporter === "string"
          ? source.reporter
          : [source.reporter?.firstName, source.reporter?.lastName]
              .filter(Boolean)
              .join(" ")
              .trim() ||
            source.reporter?.email ||
            "";

      const statusName = source.status?.name ?? source.status?.category ?? source.status ?? "Open";
      const statusId = source.statusId ?? source.status?.id ?? null;
      const statusCategory =
        source.statusCategory ??
        (typeof source.status === "object" && source.status != null
          ? source.status.category
          : null) ??
        "";
      setIssue({
        title: source.title ?? "",
        description: source.description ?? "",
        type: source.type ?? "Task",
        typeId:
          source.typeId != null && String(source.typeId).trim() !== ""
            ? String(source.typeId).trim()
            : null,
        status: statusName,
        statusId,
        statusCategory: statusCategory != null ? String(statusCategory) : "",
        priority: source.priority ?? "Medium",
        priorityId: source.priorityId ?? null,
        assignee:
          typeof source.assignee === "string"
            ? source.assignee
            : [source.assignee?.firstName, source.assignee?.lastName]
                .filter(Boolean)
                .join(" ")
                .trim() ||
              source.assignee?.email ||
              "",
        assigneeId:
          source.assigneeId ?? (typeof source.assignee === "object" ? source.assignee?.id : null),
        reporter: reporterName,
        reporterId:
          source.reporterId ??
          (typeof source.reporter === "object" ? source.reporter?.id : null) ??
          null,
        estimate: source.estimate ?? "",
        storyPoints: source.storyPoints ?? "",
        severityId: source.bugDetails?.severityId ?? source.severityId ?? null,
        severity: source.severity ?? "",
        environment: toEnvironmentDisplay(
          source.bugDetails?.environment ?? source.environment ?? ""
        ),
        reproduction:
          source.bugDetails?.reproductionSteps ??
          source.reproduction ??
          source.reproductionSteps ??
          "",
        startDate: formatDateForInput(source.startDate),
        dueDate: formatDateForInput(source.dueDate),
        progressPercent:
          source.progressPercent != null && source.progressPercent !== ""
            ? String(source.progressPercent)
            : "",
        parentId:
          source.parentId ?? (typeof source.parent === "object" ? source.parent?.id : null) ?? null,
        projectId: String(
          source.projectId ?? source.project?.id ?? source.project_id ?? projectSlug ?? ""
        ).trim(),
      });
      prevProjectIdForParentResetRef.current = String(
        source.projectId ?? source.project?.id ?? source.project_id ?? projectSlug ?? ""
      ).trim();
    }
  }, [detailSource, environmentList, projectSlug]);

  const update = (k, v) => setIssue((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const next = issue.projectId?.trim() || null;
    const prev = prevProjectIdForParentResetRef.current;
    prevProjectIdForParentResetRef.current = next;
    if (prev != null && next !== null && next !== prev) {
      statusRemapPendingRef.current = true;
      setIssue((p) => ({ ...p, parentId: null }));
    }
  }, [issue.projectId]);

  useEffect(() => {
    if (!statusRemapPendingRef.current) return;
    const statuses = getProjectWorkflowStatusesList(statusesResponse);
    if (!statuses.length) return;

    setIssue((p) => {
      const remapped = remapWorkItemStatusForTargetProject(p, statuses);
      return {
        ...p,
        statusId: remapped.statusId,
        status: remapped.status,
        statusCategory: remapped.statusCategory ?? "",
      };
    });
    statusRemapPendingRef.current = false;
  }, [statusesResponse, selectedProjectIdForUi]);

  const priorityOptions =
    priorityList && priorityList.length
      ? priorityList.map((item) => mapSystemListItemToOption(item))
      : labelsToSimpleOptions(PRIORITY_LABEL_FALLBACKS);

  const severityOptions =
    severityList && severityList.length
      ? severityList.map((item) => mapSystemListItemToOption(item))
      : labelsToSimpleOptions(SEVERITY_LABEL_FALLBACKS);

  const environmentOptions =
    environmentList && environmentList.length
      ? environmentList.map((item) => mapSystemListItemToOption(item))
      : labelsToSimpleOptions(ENVIRONMENT_LABEL_FALLBACKS);

  const assigneeOptions = getListFromApiResponse(usersData).map((user, index) =>
    mapUserToAssigneeOptionForDropdown(user, index)
  );

  const commentMentionUsers = useMemo(
    () => mapOptionsToMentionUsers(assigneeOptions),
    [assigneeOptions]
  );

  const workItemTypeOptions = useMemo(() => {
    if (!Array.isArray(workItemTypes) || workItemTypes.length === 0) return [];
    return workItemTypes.map((item) => mapSystemListItemToOption(item, TYPE_ICON_MAP));
  }, [workItemTypes]);

  const resolvedWorkItemTypeLabel = useMemo(
    () =>
      String(
        resolveWorkItemTypeDisplayName({ type: issue.type, typeId: issue.typeId }, workItemTypes)
      ).trim() || "Work item",
    [issue.type, issue.typeId, workItemTypes]
  );

  const isEpicWorkItem = useMemo(
    () =>
      String(resolvedWorkItemTypeLabel ?? "")
        .trim()
        .toLowerCase() === "epic",
    [resolvedWorkItemTypeLabel]
  );

  const resolvedWorkItemId = detailSource?.id ?? issueId;

  /** Jira-style trail: Project › Epic › Issue key · type (links / open-parent when available). */
  const hierarchyBreadcrumb = useMemo(() => {
    if (!projectSlug || !detailSource) return null;
    const projectName = breadcrumbProjectDisplayName;
    const { clientName: clientFromWorkItem } = getWorkItemClientProjectParts(detailSource);
    const projectFromList = projectsList.find(
      (p) => String(p.id ?? p.projectId) === String(projectSlug)
    );
    const clientName =
      clientFromWorkItem ||
      String(projectFromList?.clientName ?? projectFromList?.client?.name ?? "").trim();
    const currentKey = String(detailSource.issueKey ?? "").trim();
    const currentType = resolvedWorkItemTypeLabel;
    const selfId = String(detailSource.id ?? issueId ?? "").trim();
    const parentObj =
      typeof detailSource.parent === "object" && detailSource.parent != null
        ? detailSource.parent
        : null;
    const parentIdRaw =
      issue.parentId ??
      detailSource.parentId ??
      (parentObj?.id != null ? String(parentObj.id) : null);
    const parentIdNorm =
      parentIdRaw != null && String(parentIdRaw).trim() !== "" && String(parentIdRaw) !== selfId
        ? String(parentIdRaw).trim()
        : null;

    const parentDisplay = (() => {
      if (parentObj) {
        const nameFirst = String(parentObj.title ?? parentObj.summary ?? "").trim();
        if (nameFirst) return nameFirst;
        const pk = String(parentObj.itemKey ?? parentObj.issueKey ?? "").trim();
        if (pk) return pk;
        return String(detailSource.parentTitle ?? "").trim();
      }
      if (parentIdNorm && Array.isArray(parentEpicOptions)) {
        return String(
          parentEpicOptions.find((o) => String(o.value) === String(parentIdNorm))?.label ?? ""
        ).trim();
      }
      return String(detailSource.parentTitle ?? "").trim();
    })();

    const showParent = Boolean(parentIdNorm && parentDisplay);
    return {
      clientName,
      projectName,
      projectSlug: String(projectSlug),
      parentId: showParent ? parentIdNorm : null,
      parentDisplay: showParent ? parentDisplay : "",
      currentKey,
      currentType,
    };
  }, [
    projectSlug,
    detailSource,
    breadcrumbProjectDisplayName,
    projectsList,
    issue.parentId,
    issueId,
    parentEpicOptions,
    resolvedWorkItemTypeLabel,
  ]);

  const projectStatusOptions = useMemo(
    () => buildProjectStatusOptionsByCategory(statusesResponse?.data || []),
    [statusesResponse?.data]
  );

  const resolvedStatusId = useMemo(
    () =>
      resolveProjectStatusOptionValue(issue, statusesResponse?.data, projectStatusOptions) ||
      issue.statusId ||
      "",
    [issue, projectStatusOptions, statusesResponse?.data]
  );

  const dueDateMinForInput =
    issue.startDate && String(issue.startDate).trim() !== "" ? issue.startDate : undefined;

  const handleStatusChange = (newStatusId) => {
    const selected = projectStatusOptions.find((o) => String(o.value) === String(newStatusId));
    update("statusId", newStatusId || null);
    update("status", selected?.label || issue.status);
    update("statusCategory", selected?.category != null ? String(selected.category) : "");
  };

  const loadAttachments = () => {
    if (!projectSlug || !resolvedWorkItemId) return;
    dispatch(
      listWorkItemAttachments({
        projectId: String(projectSlug),
        workItemId: String(resolvedWorkItemId),
      })
    )
      .unwrap()
      .then((raw) => {
        const list = mapWorkItemAttachmentsListResponse(raw);
        setExistingAttachments(Array.isArray(list) ? list : []);
      })
      .catch(() => setExistingAttachments([]));
  };

  const handleDownloadAttachment = async (attachment) => {
    const attachmentId = attachment?.id;
    if (!projectSlug || !resolvedWorkItemId || !attachmentId) return;
    setDownloadingAttachmentId(String(attachmentId));
    try {
      const urlPayload = await dispatch(
        getWorkItemAttachmentDownloadUrl({
          projectId: String(projectSlug),
          workItemId: String(resolvedWorkItemId),
          attachmentId: String(attachmentId),
          expiresIn: 3600,
        })
      ).unwrap();
      const url = mapWorkItemAttachmentUrlResponse(urlPayload);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      showError(error || "Failed to get attachment download URL.");
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    const attachmentId = attachment?.id;
    if (!projectSlug || !resolvedWorkItemId || !attachmentId) return;
    if (!canEditWorkItem) return;
    setDeletingAttachmentId(String(attachmentId));
    try {
      await dispatch(
        deleteWorkItemAttachment({
          projectId: String(projectSlug),
          workItemId: String(resolvedWorkItemId),
          attachmentId: String(attachmentId),
        })
      ).unwrap();
      setExistingAttachments((prev) => prev.filter((a) => String(a?.id) !== String(attachmentId)));
      showSuccess("Attachment deleted");
    } catch (error) {
      showError(error?.message || error || "Failed to delete attachment");
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const handleCopyWorkItemLink = async () => {
    if (!projectSlug || !resolvedWorkItemId) {
      showError("Cannot copy link: missing project or work item.");
      return;
    }
    try {
      await copyWorkItemDetailLink({
        projectSlug,
        workItemId: resolvedWorkItemId,
      });
      showSuccess("Link copied to clipboard.");
    } catch {
      showError("Could not copy link. Check browser permissions.");
    }
  };

  const handleSave = async () => {
    if (!canEditWorkItem || !projectSlug || !detailSource) return;

    const workItemId = detailSource.id ?? issueId;
    if (!workItemId) return;

    setDateFieldErrors({});
    setParentFieldError("");

    try {
      await WorkItemPlanningDatesSchema.validate(
        {
          startDate: issue.startDate != null ? String(issue.startDate).trim() : "",
          dueDate: issue.dueDate != null ? String(issue.dueDate).trim() : "",
        },
        { abortEarly: false }
      );
    } catch (err) {
      const next = {};
      if (err?.inner?.length) {
        err.inner.forEach((e) => {
          if (e.path) next[e.path] = e.message;
        });
      }
      setDateFieldErrors(next);
      showError("Please enter valid start and due dates.");
      return;
    }

    const estimateTrimmed = String(issue.estimate ?? "").trim();
    let parsedEstimateForCap = null;
    if (estimateTrimmed) {
      parsedEstimateForCap = parseWorkItemEstimateInputToMinutes(estimateTrimmed);
      if (!parsedEstimateForCap.ok) {
        showError(parsedEstimateForCap.error || "Invalid estimate.");
        return;
      }
    }

    const sourceProjectId = String(projectSlug).trim();
    const targetProjectId = (issue.projectId && String(issue.projectId).trim()) || sourceProjectId;
    if (!targetProjectId) {
      showError("Please select a project.");
      return;
    }

    if (projectsList.length > 0 && clientsList.length > 0) {
      const project = projectsList.find(
        (p) =>
          String(p.id ?? p.projectId) === String(targetProjectId) ||
          (p.id ?? p.projectId) === targetProjectId
      );
      if (project) {
        const client = clientsList.find(
          (c) => (c.clientName ?? c.name) === (project.clientName ?? project.client?.name)
        );
        if (client && isClientOnHold(client)) {
          showError("Cannot save. This project's client is On Hold.");
          return;
        }
      }
    }

    setLoading(true);
    try {
      // Build PATCH body aligned with backend contract
      const currentVersion = detailSource.version ?? 1;
      const storyPointsValue =
        issue.storyPoints != null && String(issue.storyPoints).trim() !== ""
          ? Number(issue.storyPoints)
          : detailSource.storyPoints != null
            ? Number(detailSource.storyPoints)
            : null;

      const originalCapMinutes = parsedEstimateForCap
        ? parsedEstimateForCap.minutes
        : parseNonNegativeInt(detailSource.originalEstimateMinutes, 0);
      const remainingEstimateMinutes =
        detailSource.remainingEstimateMinutes != null
          ? parseNonNegativeInt(detailSource.remainingEstimateMinutes, originalCapMinutes)
          : originalCapMinutes;

      const progressPercent = parseNonNegativeInt(
        issue.progressPercent !== "" && issue.progressPercent != null
          ? issue.progressPercent
          : detailSource.progressPercent,
        0
      );

      const startDateForApi = toDateOnlyApiParam(issue.startDate);
      const dueDateForApi = toDateOnlyApiParam(issue.dueDate);

      const typeIdTrimmed =
        issue.typeId != null && String(issue.typeId).trim() !== ""
          ? String(issue.typeId).trim()
          : null;

      let statusIdForApi =
        resolvedStatusId ||
        issue.statusId ||
        (typeof detailSource.status === "object"
          ? detailSource.status?.id
          : detailSource.statusId) ||
        null;

      if (targetProjectId !== sourceProjectId) {
        let targetStatuses = getProjectWorkflowStatusesList(statusesResponse);
        if (!targetStatuses.length) {
          try {
            const fetched = await dispatch(
              fetchProjectStatuses({
                projectId: targetProjectId,
                context: "issue-detail-save",
              })
            ).unwrap();
            targetStatuses = getProjectWorkflowStatusesList(fetched);
          } catch {
            targetStatuses = [];
          }
        }
        const remapped = remapWorkItemStatusForTargetProject(issue, targetStatuses);
        statusIdForApi = remapped.statusId ?? statusIdForApi;
      }

      const typeLabel =
        String(issue.type ?? "").trim() ||
        resolveWorkItemTypeDisplayName(issue, workItemTypes) ||
        "";

      let isEpicType = String(typeLabel).trim().toLowerCase() === "epic";
      if (!isEpicType && typeIdTrimmed && Array.isArray(workItemTypes)) {
        const matchedType = workItemTypes.find(
          (wt) => String(wt?.id ?? wt?.value ?? "").trim() === typeIdTrimmed
        );
        if (matchedType) {
          isEpicType =
            String(matchedType?.name ?? matchedType?.value ?? "")
              .trim()
              .toLowerCase() === "epic";
        }
      }

      const isBugType = String(typeLabel).trim().toLowerCase() === "bug";

      // `issue.parentId` already holds the current value (seeded from detailSource on load,
      // then updated on every change) — falling back past an explicit `null` here would treat
      // "user cleared the field" the same as "never loaded" and silently restore the old epic.
      const parentIdForBody = isEpicType ? undefined : (issue.parentId ?? null);

      if (!isEpicType) {
        const parentIdTrimmed = parentIdForBody != null ? String(parentIdForBody).trim() : "";
        if (!parentIdTrimmed) {
          setParentFieldError("Parent epic is required");
          showError("Parent epic is required.");
          return;
        }
      }

      const body = buildWorkItemSubmitBody({
        version: currentVersion,
        typeId: typeIdTrimmed,
        typeLabel,
        title: issue.title ?? "",
        description:
          issue.description != null && String(issue.description).trim() !== ""
            ? issue.description
            : null,
        statusId: statusIdForApi,
        priorityId:
          issue.priorityId ??
          (typeof detailSource.priority === "object"
            ? detailSource.priority?.id
            : detailSource.priorityId) ??
          null,
        assigneeId: issue.assigneeId ?? detailSource.assigneeId ?? null,
        parentId: parentIdForBody,
        originalEstimate: estimateTrimmed,
        remainingEstimateMinutes,
        storyPoints:
          storyPointsValue != null && !Number.isNaN(Number(storyPointsValue))
            ? Number(storyPointsValue)
            : 0,
        startDate: startDateForApi,
        dueDate: dueDateForApi,
        reporterId: issue.reporterId ?? detailSource?.reporterId ?? null,
        progressPercent,
        projectId: targetProjectId !== sourceProjectId ? targetProjectId : undefined,
        bugDetails: isBugType ? buildBugDetailsFromIssueState(issue) : undefined,
      });

      const filesToUpload = Array.isArray(attachments)
        ? attachments.map((item) => item?.file ?? item).filter(Boolean)
        : [];

      await dispatch(
        updateWorkItem({
          projectId: sourceProjectId,
          workItemId,
          body,
          ...(filesToUpload.length > 0 ? { files: filesToUpload } : {}),
        })
      ).unwrap();

      if (filesToUpload.length > 0) {
        setAttachments([]);
        setAttachmentDropzoneKey((k) => k + 1);
        loadAttachments();
      }

      setDateFieldErrors({});
      if (targetProjectId !== sourceProjectId) {
        setWorkItemById((prev) =>
          prev
            ? {
                ...prev,
                projectId: targetProjectId,
                ...(prev.project && typeof prev.project === "object"
                  ? { project: { ...prev.project, id: targetProjectId } }
                  : {}),
              }
            : prev
        );
      }
      showSuccess("Work item updated successfully.");
      setActivityRefreshKey((prev) => prev + 1);
      if (targetProjectId !== sourceProjectId && typeof onProjectIdChange === "function") {
        onProjectIdChange(targetProjectId);
      }
      if (typeof onWorkItemSaved === "function") {
        try {
          await Promise.resolve(onWorkItemSaved());
        } catch {
          /* host handles errors; save already succeeded */
        }
      }
    } catch (error) {
      if (isWorkItemVersionConflictError(error)) {
        showError("This work item was updated elsewhere. Loading the latest version…");
        try {
          const workItemRaw = await dispatch(
            fetchWorkItemById({ projectId: sourceProjectId, workItemId: String(workItemId) })
          ).unwrap();
          setWorkItemById(mapWorkItemFromApiPayload(workItemRaw));
        } catch {
          /* refetch failed — user can reload manually */
        }
        return;
      }
      showError(error?.message || "Failed to update work item.");
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    if (presentation === "modal") {
      /* Host {@link WorkItemDetailModal} draws the visible loader overlay. */
      return (
        <div
          className="min-h-[min(70vh,560px)] w-full shrink-0"
          aria-busy="true"
          aria-label="Loading work item"
        />
      );
    }
    return (
      <div className="issue-layout flex min-h-[400px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  const isStandalone = presentation === "standalone";

  if (bootstrapError) {
    const modalError =
      presentation === "modal"
        ? "flex min-h-[min(50vh,400px)] w-full flex-col items-center justify-center gap-2 bg-white px-4 py-8 text-center"
        : "issue-layout flex flex-col justify-center items-center min-h-[400px] gap-2 px-4 text-center";
    return (
      <div className={modalError}>
        <Typography variant="text1" className="text-text-secondary">
          {bootstrapError}
        </Typography>
      </div>
    );
  }

  return (
    <div className={isStandalone ? "issue-layout issue-layout--standalone" : "issue-layout"}>
      {hierarchyBreadcrumb ? (
        <nav
          className="work-item-breadcrumb flex flex-wrap items-center gap-x-3 gap-y-2 text-sm border-b border-border-primary pb-3 mb-3"
          aria-label="Work item hierarchy"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium text-text-primary inline-flex items-center gap-1.5">
              {[hierarchyBreadcrumb.clientName, hierarchyBreadcrumb.projectName]
                .filter(Boolean)
                .join(" - ")}
            </span>
            {hierarchyBreadcrumb.parentDisplay ? (
              <>
                <span className="text-text-secondary select-none" aria-hidden>
                  ›
                </span>
                <span className="inline-flex min-w-0 items-center gap-1.5 text-text-primary">
                  <span className="inline-flex shrink-0" aria-hidden>
                    <WorkItemTypeIcon typeLabel="Epic" sizeClassName="text-[1.05rem]" />
                  </span>
                  {onOpenRelatedWorkItem && hierarchyBreadcrumb.parentId ? (
                    <button
                      type="button"
                      className="truncate rounded-sm bg-transparent p-0 text-left font-sans text-sm font-medium text-text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4c9aff]"
                      onClick={() => onOpenRelatedWorkItem(hierarchyBreadcrumb.parentId)}
                    >
                      {hierarchyBreadcrumb.parentDisplay}
                    </button>
                  ) : (
                    <span className="truncate font-medium text-text-primary">
                      {hierarchyBreadcrumb.parentDisplay}
                    </span>
                  )}
                </span>
              </>
            ) : null}
            <span className="text-text-secondary select-none" aria-hidden>
              ›
            </span>
            <span className="inline-flex min-w-0 items-center gap-1.5 text-text-primary">
              <span className="inline-flex shrink-0" aria-hidden>
                <WorkItemTypeIcon
                  typeLabel={hierarchyBreadcrumb.currentType}
                  sizeClassName="text-[1.05rem]"
                />
              </span>
              <span className="truncate font-semibold">
                {hierarchyBreadcrumb.currentKey || "—"}
              </span>
              <span className="whitespace-nowrap font-normal text-text-primary">
                · {hierarchyBreadcrumb.currentType}
              </span>
              {projectSlug && resolvedWorkItemId ? (
                <WorkItemCopyLinkButton onClick={handleCopyWorkItemLink} />
              ) : null}
            </span>
          </div>
        </nav>
      ) : null}
      {/* HEADER: show issue key only (never raw UUID); title in input below */}
      <header className={loading ? "relative z-[10001]" : undefined}>
        {!hierarchyBreadcrumb ? (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Typography variant="h2" component="h2" className="!mb-0">
              {detailSource?.issueKey ?? workItemByIdError ?? ""}
            </Typography>
            {resolvedWorkItemTypeLabel ? (
              <span className="text-sm font-normal text-text-secondary">
                · {resolvedWorkItemTypeLabel}
              </span>
            ) : null}
            {projectSlug && resolvedWorkItemId ? (
              <WorkItemCopyLinkButton onClick={handleCopyWorkItemLink} />
            ) : null}
          </div>
        ) : (
          <Typography variant="h2" component="h2">
            {detailSource?.issueKey ?? workItemByIdError ?? ""}
          </Typography>
        )}
        <Input
          label="Title"
          value={issue.title}
          onChange={(e) => update("title", e.target.value)}
          noWrapper={true}
          disabled={loading || readOnlyWorkItem}
        />
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={loading || readOnlyWorkItem}
          className="!px-3 !py-1.5"
          onClick={handleSave}
        >
          {loading ? "Saving" : "Save"}
        </Button>
      </header>

      <div className="content">
        {/* LEFT */}
        <main>
          <Typography variant="h5">Description</Typography>
          <MiniEditor
            placeholder="Add a description..."
            value={issue.description}
            onChange={(html) => update("description", html)}
            showToolbar={true}
            showActions={false}
            minHeight="120px"
            enableImageResize={true}
            disabled={readOnlyWorkItem || loading}
          />

          {isEpicWorkItem && selectedProjectIdForUi && resolvedWorkItemId ? (
            <EpicChildIssuesPanel
              projectSlug={selectedProjectIdForUi}
              epicWorkItemId={String(resolvedWorkItemId)}
              workItemTypes={workItemTypes}
              priorityOptions={priorityOptions}
              severityList={severityList}
              assigneeOptions={assigneeOptions}
              onOpenChildWorkItem={(childId) => {
                if (onOpenRelatedWorkItem && childId) {
                  onOpenRelatedWorkItem(childId, { fromEpicChild: true });
                }
              }}
              canCreateWorkItem={canCreateWorkItem}
            />
          ) : null}

          {/* File Upload Panel */}
          <FileUploadPanel
            onFilesChange={setAttachments}
            maxFiles={10}
            maxSizeMB={10}
            existingAttachments={existingAttachments}
            onDownloadExisting={handleDownloadAttachment}
            downloadingAttachmentId={downloadingAttachmentId}
            onDeleteExisting={readOnlyWorkItem ? undefined : handleDeleteAttachment}
            deletingAttachmentId={deletingAttachmentId}
            readOnly={readOnlyWorkItem}
            fileDropzoneKey={attachmentDropzoneKey}
          />

          <ActivityTabs
            history={[]}
            projectId={activityProjectId}
            workItemId={detailSource?.id ?? issueId}
            workItemKey={detailSource?.issueKey ?? ""}
            workItemTitle={issue.title ?? detailSource?.title ?? ""}
            initialComments={detailSource?.comments ?? []}
            refreshKey={activityRefreshKey}
            mentionUsers={commentMentionUsers}
            onRefresh={() => setActivityRefreshKey((prev) => prev + 1)}
          />
        </main>

        {/* RIGHT */}
        <aside>
          <Dropdown
            label="Project"
            name="projectId"
            value={issue.projectId || selectedProjectIdForUi || ""}
            options={projectOptions}
            onChange={(e) => {
              const value = e.target.value;
              update("projectId", value || "");
            }}
            placeholder={
              projectsLoadingOrEmpty && projectOptions.length === 0
                ? "Loading projects..."
                : "Select project"
            }
            disabled={readOnlyWorkItem || loading}
            searchable={true}
            required
          />
          {/* Parent — required for non-epic work items (Task, Bug, …) */}
          {!isEpicWorkItem ? (
            <>
              <Dropdown
                label="Parent"
                name="parentId"
                value={issue.parentId || ""}
                options={parentEpicOptions}
                onChange={(e) => {
                  const value = e.target.value;
                  update("parentId", value || null);
                  setParentFieldError("");
                }}
                placeholder="Select parent (Epic)"
                disabled={readOnlyWorkItem || loading}
                required
              />
              <ErrorMsg message={parentFieldError} />
            </>
          ) : null}
          <Dropdown
            label="Work type"
            name="typeId"
            value={issue.typeId || ""}
            options={workItemTypeOptions}
            onChange={(e) => {
              const value = e.target.value;
              const found = workItemTypeOptions.find((o) => String(o.value) === String(value));
              const label = found?.label != null ? String(found.label) : "";
              update("typeId", value || null);
              update("type", label);
              if (label.trim().toLowerCase() !== "bug") {
                update("severityId", null);
                update("severity", "");
                update("environment", "");
                update("reproduction", "");
              }
            }}
            placeholder={workItemTypeOptions.length === 0 ? "Loading types…" : "Select work type"}
            disabled={readOnlyWorkItem || loading || workItemTypeOptions.length === 0}
            searchable={true}
          />
          <Dropdown
            label="Status"
            name="status"
            value={resolvedStatusId}
            options={
              projectStatusOptions.length
                ? projectStatusOptions
                : DEFAULT_WORK_ITEM_STATUS_CATEGORY_FILTER_OPTIONS
            }
            onChange={(e) => {
              if (e.target.value) {
                handleStatusChange(e.target.value);
              }
            }}
            placeholder="Select status"
            disabled={readOnlyWorkItem || loading}
            preserveOptionOrder={true}
          />
          <Dropdown
            label="Priority"
            name="priority"
            value={issue.priorityId || issue.priority}
            options={priorityOptions}
            onChange={(e) => {
              const value = e.target.value;
              const found = priorityOptions.find((o) => String(o.value) === String(value));
              update("priorityId", value || null);
              update("priority", found?.label || value || "");
            }}
            placeholder="Select priority"
            disabled={readOnlyWorkItem || loading}
          />

          {/* Assignment */}
          <Typography variant="h5">Assignment</Typography>
          <Dropdown
            label="Assignee"
            name="assignee"
            value={issue.assigneeId || ""}
            options={assigneeOptions}
            onChange={(e) => {
              const value = e.target.value;
              const found = assigneeOptions.find((o) => String(o.value) === String(value));
              update("assigneeId", value || null);
              update("assignee", found?.label || "");
            }}
            placeholder="Select assignee"
            disabled={readOnlyWorkItem || loading}
            searchable={true}
          />
          <Dropdown
            label="Reporter"
            name="reporter"
            value={issue.reporterId || ""}
            options={assigneeOptions}
            onChange={(e) => {
              const value = e.target.value;
              const found = assigneeOptions.find((o) => String(o.value) === String(value));
              update("reporterId", value || null);
              update("reporter", found?.label || "");
            }}
            placeholder="Select reporter"
            disabled={readOnlyWorkItem || loading}
            searchable={true}
          />

          {/* Planning */}
          <Input
            label="Original estimate"
            type="text"
            autoComplete="off"
            value={issue.estimate}
            onChange={(e) => update("estimate", sanitizeWorkItemEstimateInput(e.target.value))}
            disabled={readOnlyWorkItem || loading}
            placeholder="e.g. 2w, 5h, 1d 2h, 480 (minutes)"
            className="m-0 p-1"
          />
          <div>
            <Typography
              variant="label"
              className="block mb-1 text-sm font-medium text-text-primary"
            >
              Start Date
            </Typography>
            <SingleDatePicker
              value={issue.startDate ? { date: issue.startDate } : null}
              onChange={(val) => {
                update("startDate", val?.date ?? "");
                setDateFieldErrors((p) => {
                  const next = { ...p };
                  delete next.startDate;
                  return next;
                });
              }}
              disabled={readOnlyWorkItem || loading}
              placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
              className={dateFieldErrors.startDate ? "!border-red-500" : ""}
            />
            <ErrorMsg message={dateFieldErrors.startDate} />
          </div>
          <div>
            <Typography
              variant="label"
              className="block mb-1 text-sm font-medium text-text-primary"
            >
              Due Date
            </Typography>
            <SingleDatePicker
              value={issue.dueDate ? { date: issue.dueDate } : null}
              onChange={(val) => {
                update("dueDate", val?.date ?? "");
                setDateFieldErrors((p) => {
                  const next = { ...p };
                  delete next.dueDate;
                  return next;
                });
              }}
              minDate={dueDateMinForInput}
              disabled={readOnlyWorkItem || loading}
              placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
              className={dateFieldErrors.dueDate ? "!border-red-500" : ""}
            />
            <ErrorMsg message={dateFieldErrors.dueDate} />
          </div>

          {/* Bug-only */}
          {String(resolvedWorkItemTypeLabel ?? "")
            .trim()
            .toLowerCase() === "bug" && (
            <>
              <Typography variant="h5">Bug Details</Typography>
              <Dropdown
                label="Severity"
                name="severity"
                value={issue.severityId || issue.severity}
                options={severityOptions}
                onChange={(e) => {
                  const value = e.target.value;
                  const found = severityOptions.find((o) => String(o.value) === String(value));
                  update("severityId", value || null);
                  update("severity", found?.label || value || "");
                }}
                placeholder="Select severity"
                disabled={readOnlyWorkItem || loading}
              />
              <Dropdown
                label="Environment"
                name="environment"
                value={issue.environment}
                options={environmentOptions}
                onChange={(e) => update("environment", e.target.value)}
                placeholder="Select environment"
                disabled={readOnlyWorkItem || loading}
              />
              <Input
                type="textarea"
                label="Reproduction steps"
                placeholder="Reproduction steps (multiple lines)"
                value={issue.reproduction ?? ""}
                onChange={(e) => update("reproduction", e.target.value)}
                rows={5}
                className="min-h-[100px] resize-y"
                disabled={readOnlyWorkItem || loading}
              />
            </>
          )}

          {/* Time Tracking */}
          <div className="time-box">
            <Typography variant="span2" component="p">
              Today: {formatCompactLoggedDuration(loggedTimeSummary.todayMinutes)}
            </Typography>
            <Typography variant="span2" component="p">
              This week: {formatCompactLoggedDuration(loggedTimeSummary.weekMinutes)}
            </Typography>
            <Button
              size="sm"
              disabled={loading}
              onClick={() => {
                if (!canLogTimeEntry) {
                  setTimeEntryDeniedKey(PERMISSION_KEYS.TIME_ENTRY_CREATE);
                  return;
                }
                setIsLogTimeModalOpen(true);
              }}
            >
              Log Time
            </Button>
          </div>
        </aside>
      </div>

      {/* Log Time Modal */}
      <LogTimeModal
        isOpen={isLogTimeModalOpen}
        onClose={() => setIsLogTimeModalOpen(false)}
        issue={detailSource ?? { issueKey: issueId, title: issue.title }}
        projectId={activityProjectId}
        workItemId={detailSource?.id ?? issueId}
        onSuccess={async () => {
          const workItemId = detailSource?.id ?? issueId;
          if (activityProjectId && workItemId) {
            try {
              const raw = await dispatch(
                fetchWorkItemById({ projectId: activityProjectId, workItemId })
              ).unwrap();
              setWorkItemById(mapWorkItemFromApiPayload(raw));
            } catch {
              // ignore — still refresh activity tabs below
            }
          }
          setActivityRefreshKey((prev) => prev + 1);
        }}
      />

      <PermissionDeniedModal
        isOpen={timeEntryDeniedKey != null}
        onClose={() => setTimeEntryDeniedKey(null)}
        permissionKey={timeEntryDeniedKey}
      />

      {loading ? (
        <div
          className="fixed inset-0 z-[10000] bg-transparent"
          aria-busy="true"
          aria-live="polite"
        />
      ) : null}
    </div>
  );
}
